import { ComponentProps, ReactNode, useId } from 'react';
import { Typography } from '../typography/typography';
import { twMerge } from 'tailwind-merge';
import {
  Button,
  Disclosure,
  DisclosureGroup,
  DisclosurePanel,
  Heading,
} from 'react-aria-components';
import { IconAngleDown, IconPlus } from '@maia/icons';
import { ActionButton } from '../actionButton/actionButton';

export type IAccordion = {
  title: string;
  content?: ReactNode;
  childs?: Array<IAccordion>;
};

type AccordionGroupProps = IAccordion & {
  className?: string;
} & Omit<ComponentProps<typeof DisclosureGroup>, 'children' | 'className'>;

export function AccordionGroup(props: AccordionGroupProps) {
  const id = useId();
  const { title, content, childs, ...rest } = props;

  return (
    <DisclosureGroup {...rest}>
      <Disclosure className="rounded-12 border border-primary100">
        {({ isExpanded }) => (
          <>
            <Heading>
              <Button
                slot="trigger"
                className={twMerge(
                  'w-full rounded-12 text-start flex flex-row items-center py-20 px-24 transition-colors ease-out duration-200 justify-between outline-none',
                  "data-[hovered='true']:bg-primary50 data-[focus-visible='true']:bg-primary50",
                )}
              >
                <Typography
                  variant="body-m"
                  weight="medium"
                  className="text-grayscale600"
                >
                  {title}
                </Typography>
                <IconAngleDown
                  className={twMerge(
                    'min-w-24 w-24 h-24 fill-info300 transition-transform ease-out duration-200',
                    isExpanded && 'rotate-180',
                  )}
                />
              </Button>
            </Heading>

            {isExpanded && (
              <DisclosurePanel className="px-24 pb-20 space-y-4">
                {childs && childs?.length > 0
                  ? childs.map((child, key) => (
                      <SubAccordion
                        key={`accordion-child-${id}-${key}`}
                        {...child}
                      />
                    ))
                  : content}
              </DisclosurePanel>
            )}
          </>
        )}
      </Disclosure>
    </DisclosureGroup>
  );
}

function SubAccordion(props: IAccordion) {
  const id = useId();

  const { title, content, childs } = props;

  return (
    <DisclosureGroup
      className={({ state }) =>
        twMerge(state.expandedKeys.size > 0 && 'bg-primary50 rounded-12')
      }
    >
      <Disclosure className={twMerge('rounded-12')}>
        {({ isExpanded, state: { toggle } }) => (
          <>
            <Heading className="">
              <Button
                slot="trigger"
                className='w-full rounded-12 group text-start flex flex-row items-center p-12 space-x-24 transition-colors ease-out duration-200 outline-none data-[hovered="true"]:bg-primary50 data-[focus-visible="true"]:bg-primary50'
              >
                <ActionButton
                  icon={IconPlus}
                  size="large"
                  className={twMerge(
                    'group-focus-visible:bg-primary100 group-hover:bg-primary100',
                    isExpanded && 'bg-grayscale0',
                  )}
                  onPress={toggle}
                  excludeFromTabOrder
                />
                <Typography
                  variant="body-m"
                  weight="medium"
                  className="text-grayscale600"
                >
                  {title}
                </Typography>
              </Button>
            </Heading>

            {isExpanded && (
              <DisclosurePanel className="pr-24 pb-20 pl-56">
                {childs && childs?.length > 0
                  ? childs.map((child, key) => (
                      <SubAccordion
                        key={`accordion-nested-child-${id}-${key}`}
                        {...child}
                      />
                    ))
                  : content}
              </DisclosurePanel>
            )}
          </>
        )}
      </Disclosure>
    </DisclosureGroup>
  );
}
